import type { BlogPost } from "../blogTypes";

export const serviceCostsNetherlandsWhatTenantsShouldKnow: BlogPost = {
    slug: "service-costs-netherlands-what-tenants-should-know",
    title: "Service Costs in the Netherlands: What Tenants Should Know",
    metaTitle: "Service Costs Netherlands | Tenant Guide to Servicekosten",
    metaDescription: "Service costs in the Netherlands: what landlords may charge in 2026, administration limits, annual statements and how tenants can dispute costs.",
    excerpt: "Service costs can add hundreds of euros to your monthly rent — but not everything a landlord lists is a legitimate service cost. Here's what Dutch law actually allows and how to challenge unfair charges.",
    date: "2026-01-02",
    updated: "2026-08-23",
    readTime: "4 min read",
    category: "Rental Law",
    keywords: ["service costs Netherlands", "servicekosten huur", "landlord service charges Dutch", "utility costs rental Netherlands"],
    content: `
## What Are Service Costs?

Service costs (servicekosten) are charges on top of your base rent (kale huur) that cover shared services and utilities actually provided to you. These can include:

- **Gas, water, and electricity** (if supplied through the landlord)
- **Cleaning and lighting of common areas** (hallways, stairwells)
- **Garden upkeep**
- **A caretaker (huismeester)**
- **Use of furniture or floor coverings**
- **Specific supplied services**, such as a lift's alarm-line subscription, refuse bags, transport of waste to the public road, or use of a container

Service costs are separate from your base rent and follow different rules. See the [Huurcommissie service-cost policy](https://www.huurcommissie.nl/support/beleidsboeken/servicekosten/overige-servicekosten) for the full list of what qualifies.

## What Is Not a Legitimate Service Cost

Some items commonly appear on service-cost statements but should not be charged to tenants this way:

- **Building insurance (opstalverzekering) is not a tenant service cost.** Property, fire, storm, business-interruption and landlord liability insurance remain the landlord's own cost. Only limited tenant-benefit insurance, such as glass insurance or insurance for rented movable contents, may be passed on.
- **General elevator maintenance or repair is not a service cost.** Owner maintenance and repair obligations cannot simply be passed through to tenants; only a specific supplied tenant service (such as an alarm-line subscription) can qualify.
- **Waste taxes and levies are not service costs.** Only specific supplied services, such as refuse bags or container use, can be charged this way.

## What Can Landlords Charge?

**Legal rule:** landlords can only charge service costs for **actual services provided**, based on **actual costs**, not as a way to increase your total rent beyond legal limits. Key requirements:

- Costs must be **reasonable and proportional**
- The landlord must provide an **annual itemised statement (jaarafrekening)** for the previous calendar year **before 1 July**
- You have the right to **inspect the underlying invoices and supporting records**
- Any overpayment must be **refunded** to you

Administration costs are not automatically invalid. Under [Huurcommissie policy](https://www.huurcommissie.nl/support/beleidsboeken/servicekosten/overige-servicekosten), limited administration costs can be allowed — generally up to 2% for heating costs, 1% where measurement and allocation are outsourced, and 5% for other items, subject to the current minimum and maximum per statement. What is not allowed is a vague, unexplained administration charge with no actual-cost basis or an added profit margin.

## Common Unfair Practices

Watch out for these red flags in service-cost clauses:

- **Fixed monthly amounts with no annual settlement** — you're entitled to a reconciliation against actual costs
- **Vague descriptions**, such as unexplained "administrative costs" with no breakdown
- **A profit margin** added on top of actual costs
- **Landlord maintenance or repair obligations disguised as service costs**, such as general elevator maintenance or building insurance
- **No breakdown provided** — just a lump sum added to rent

## How to Challenge Unfair Service Costs

If you believe your service costs are unreasonable:

1. **Request the annual statement** — your landlord must provide this before 1 July for the previous year
2. **Ask for the supporting invoices** — you can verify the actual costs
3. **Write a formal objection** — dispute specific charges in writing
4. **Contact the Huurcommissie** — the Rent Tribunal can assess whether charges are reasonable, though whether its decision is binding depends on your sector and contract date
5. **Reclaim overpayments** — if costs were too high, you're entitled to a refund

## Social Housing vs Middle-Rent vs Free Sector

For **social housing**, and for **middle-rent or free-sector contracts entered into on or after 1 July 2024**, the [Huurcommissie can issue a binding service-cost decision](https://www.huurcommissie.nl/onderwerpen/huurder-sociale--en-middensector/servicekosten-sociale-middensector-huurder/jaarafrekening-servicekosten-beoordelen). For a **free-sector contract entered into before 1 July 2024**, the Huurcommissie generally requires a written agreement between the parties to hear the matter and gives an advisory result only; otherwise the route is the kantonrechter (sub-district court).

## Check Your Service Costs

Not sure if your service costs are fair? [Upload your contract to RentSense NL](/app) and we'll analyze your service-cost clause for €7.99, developed with reference to current official Dutch government and Huurcommissie sources.

## Sources checked

- [Huurcommissie service-cost policy](https://www.huurcommissie.nl/support/beleidsboeken/servicekosten/overige-servicekosten)
- [Huurcommissie annual service-cost procedure](https://www.huurcommissie.nl/onderwerpen/huurder-sociale--en-middensector/servicekosten-sociale-middensector-huurder/jaarafrekening-servicekosten-beoordelen)
    `,
};
